import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours } from "../useBusinessInfo";
import "./opal-store.css";

/* "Why buy direct" cards — copy verbatim from the design */
const BUY_DIRECT_CARDS = [
  {
    kicker: "BEST PRICES",
    kickerClass: "",
    title: "Guaranteed value",
    text: "Buying at the source means the best prices on genuine Coober Pedy opal.",
  },
  {
    kicker: "AUTHENTIC",
    kickerClass: "os-kick--teal",
    title: "Certificate included",
    text: "Each opal comes with a certificate of authenticity.",
  },
  {
    kicker: "FAMILY OWNED",
    kickerClass: "os-kick--turquoise",
    title: "Since the 1920s",
    text: "Supporting local mining families for over a century.",
  },
  {
    kicker: "EXPERT GUIDANCE",
    kickerClass: "os-kick--blue",
    title: "Know your stone",
    text: "Personal consultation from people who mine and cut the opal themselves.",
  },
];

/* Opal collection cards (5 opal types + custom jewellery) — copy verbatim from the design */
const COLLECTION_CARDS = [
  {
    kicker: "💎 CLASSIC",
    kickerClass: "",
    title: "White Opal",
    text: "Classic Australian opal with brilliant colour play. Affordable to premium.",
  },
  {
    kicker: "⚫ RARE",
    kickerClass: "os-kick--teal",
    title: "Black Opal",
    text: "Rare dark body tone that makes colour leap. Premium to exceptional.",
  },
  {
    kicker: "🪨 NATURAL",
    kickerClass: "os-kick--turquoise",
    title: "Boulder Opal",
    text: "Natural ironstone backing. Mid-range to premium.",
  },
  {
    kicker: "✨ TRANSPARENT",
    kickerClass: "os-kick--blue",
    title: "Crystal Opal",
    text: "Transparent body with vivid internal fire. Mid-range to premium.",
  },
  {
    kicker: "🐚 COLLECTOR",
    kickerClass: "",
    title: "Opal Curios",
    text: "Opalised fossils, shells and unique specimens. Collector items.",
  },
  {
    kicker: "🛠️ CUSTOM",
    kickerClass: "os-kick--teal",
    title: "Custom Jewellery",
    text: "Handcrafted on our premises — choose your opal and metal.",
  },
];

/* Photo strip between the collection and the CTA — images verbatim from the design */
const GALLERY_IMAGES = [
  "/design/assets/store-2.jpg",
  "/design/assets/store-3.jpg",
  "/design/assets/prod-1.jpg",
  "/design/assets/prod-2.jpg",
  "/design/assets/prod-3.jpg",
  "/design/assets/prod-5.jpg",
];

export default function ROpalStorePage() {
  const openingHours = useOpeningHours();

  return (
    <MarketingShell pageClass="pg-opal-store">
      <SEOHead
        title="Opal Shop"
        fullTitle="Opal Shop Coober Pedy – Buy Direct from the Mine | Umoona"
        description="Genuine Australian opal direct from Umoona's Coober Pedy mine, each piece with a certificate of authenticity. Visit the showroom or shop online."
        keywords="buy opals Coober Pedy, direct from mine, authentic Australian opals, opal shop, family owned opal mine"
        canonical="/opal-store"
        ogImage="/og-umoona.jpg"
        url="https://umoonaopalmine.com.au/opal-store"
      />

      {/* HERO */}
      <div className="u-hero">
        <img className="u-hero__img" src="/design/assets/store-1.jpg" alt="Authentic Coober Pedy opals on display at the Umoona opal store" />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Buy direct from the mine · Best prices guaranteed</div>
          <h1 className="u-hero__title">Authentic Australian Opals, Straight from the Source</h1>
          <p className="u-hero__sub">
            Just pure, authentic Coober Pedy opals from local miners. Experience the difference of
            buying direct from the people who know and love these precious stones.
          </p>
        </div>
      </div>

      {/* WHY BUY DIRECT */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">Why buy direct from Umoona?</div>
        <h2 className="u-h2">No middlemen — just miners</h2>
        <div className="u-grid2">
          {BUY_DIRECT_CARDS.map((card) => (
            <div className="os-card" key={card.title}>
              <div className={"os-card__kicker " + card.kickerClass}>{card.kicker}</div>
              <div className="os-card__title">{card.title}</div>
              <p className="os-card__text">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OPAL COLLECTION */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Our opal collection</div>
        <h2 className="u-h2">Five kinds of Coober Pedy colour</h2>
        <div className="u-grid3">
          {COLLECTION_CARDS.map((card) => (
            <div className="os-card" key={card.title}>
              <div className={"os-card__kicker " + card.kickerClass}>{card.kicker}</div>
              <div className="os-card__title">{card.title}</div>
              <p className="os-card__text">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PHOTO STRIP */}
      <section className="os-gallery">
        <div className="u-grid3 os-gallery__grid">
          {GALLERY_IMAGES.map((src) => (
            <div className="os-gallery__tile" key={src}>
              <img src={src} alt="Authentic Coober Pedy opals and opal jewellery from the Umoona store" loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Experience authentic Australian opals today</h2>
          <p className="u-ctacenter__sub">
            Shop our online store, or visit us in Coober Pedy to see the collection in person.
          </p>
          {openingHours.closed_on_tuesdays && (
            <p className="os-tuesday-note">⚠️ Closed on Tuesdays</p>
          )}
          <div className="u-ctacenter__btns">
            <Link to="/online-store" className="u-btn u-btn--primary">
              Shop Online Store
            </Link>
            <Link to="/contact" className="u-btn u-btn--ghost">
              Visit Us in Coober Pedy
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
